import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useFavorites } from '@/hooks/useFavorites';
import { QuoteCard } from '@/components/quote/QuoteCard';
import { QuoteSkeleton, Skeleton } from '@/components/ui/Skeleton';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

export function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading } = useQuery<{ success: boolean; data: any }>({
    queryKey: ['category', slug],
    queryFn: () => api.categories.getBySlug(slug!),
    enabled: !!slug,
  });
  const { data: favData } = useFavorites();

  const category = data?.data;
  const quotes = category?.quotes || [];
  const favorites = new Set((favData?.data || []).map((f: any) => f.quote_id || f.id));

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-12">
        <Skeleton className="h-10 w-48 mb-8" />
        <div className="grid md:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <QuoteSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <p className="text-xl text-surface-500">Category not found</p>
        <Link to="/categories" className="btn-primary mt-4 inline-flex">Browse Categories</Link>
      </div>
    );
  }

  return (
    <motion.div initial="initial" animate="animate" className="max-w-6xl mx-auto px-4 py-12">
      <motion.div variants={fadeUp}>
        <Link to="/categories" className="inline-flex items-center gap-2 text-sm text-surface-500 hover:text-surface-900 transition-colors mb-6">
          <ArrowLeft size={16} />
          All Categories
        </Link>
        <h1 className="text-4xl font-bold mb-2">{category.name}</h1>
        <p className="text-surface-500 mb-8">{quotes.length} quotes</p>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-4">
        {quotes.map((quote: any, index: number) => (
          <motion.div
            key={quote.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <Link to={`/quote/${quote.id}`}>
              <QuoteCard quote={quote} isFavorited={favorites.has(quote.id)} />
            </Link>
          </motion.div>
        ))}
      </div>

      {quotes.length === 0 && (
        <div className="text-center py-16">
          <p className="text-xl text-surface-500">No quotes in this category yet</p>
        </div>
      )}
    </motion.div>
  );
}
